import {Dispatch, FC, SetStateAction, useEffect, useMemo, useRef} from 'react'
import {useTranslation} from '@/translations/i18n.tsx'
import {useTheme} from 'styled-components'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {MenuButton, MenuProvider} from '@ariakit/react'
import {Checkbox} from '@components/commons/checkbox/ChekBox.tsx'
import {Menu, MenuGroup, MenuHeading} from '@components/ui/menu-atoms/MenuAtoms.tsx'
import {StyledSelectMenuItem} from '@components/ui/select-column-header/style.ts'

interface SelectColumnHeaderProps {
    currentPageIds: string[]
    isSelectAll: boolean
    selectedIds: string[]
    unselectedIds: string[]
    setIsSelectAll: Dispatch<SetStateAction<boolean>>
    setSelectedIds: Dispatch<SetStateAction<string[]>>
    setUnselectedIds: Dispatch<SetStateAction<string[]>>
    selectedCount: number
    paginationTotal: number
}
export const SelectColumnHeader: FC<SelectColumnHeaderProps> = ({
    currentPageIds,
    isSelectAll,
    setIsSelectAll,
    selectedIds,
    unselectedIds,
    setSelectedIds,
    setUnselectedIds,
    selectedCount,
    paginationTotal
}) => {
    const {t} = useTranslation()
    const theme = useTheme()
    const checkboxRef = useRef<HTMLInputElement>(null)
    const isCheckboxChecked = useMemo(() => isSelectAll || selectedIds.length >= 1, [isSelectAll, selectedIds.length])

    useEffect(() => {
        if (checkboxRef.current) {
            if (
                (!checkboxRef.current.indeterminate && isSelectAll && unselectedIds.length >= 1) ||
                (!isSelectAll && selectedIds.length >= 1 && selectedIds.length < paginationTotal)
            ) {
                checkboxRef.current.indeterminate = true
            }
        }
    }, [isSelectAll, unselectedIds.length, selectedIds.length, paginationTotal])
    return (
        <Flexbox
            fullWidth
            justify="flex-end"
            style={{
                padding: theme.spacing * 0.5
            }}
        >
            <MenuProvider placement="right-start">
                <MenuButton
                    render={
                        <Checkbox
                            disabled={paginationTotal == 0}
                            id="select-all"
                            checked={isCheckboxChecked}
                            onChange={
                                isSelectAll || selectedIds.length >= 1
                                    ? () => {
                                          setIsSelectAll(false)
                                          setSelectedIds([])
                                          setUnselectedIds([])
                                      }
                                    : event => event.preventDefault()
                            }
                            ref={checkboxRef}
                        />
                    }
                />
                <Menu width="220px">
                    <MenuHeading>{t('commons:select')}</MenuHeading>
                    <MenuGroup>
                        <StyledSelectMenuItem
                            onClick={() => {
                                if (
                                    isSelectAll &&
                                    unselectedIds.some(unselectedId => currentPageIds.includes(unselectedId))
                                ) {
                                    setUnselectedIds(prevState =>
                                        prevState.filter(unselectedId => !currentPageIds.includes(unselectedId))
                                    )
                                } else if (isSelectAll) {
                                    setUnselectedIds(prevState => [
                                        ...prevState.filter(unselectedId => !currentPageIds.includes(unselectedId)),
                                        ...currentPageIds
                                    ])
                                } else if (currentPageIds.every(currentPageId => selectedIds.includes(currentPageId))) {
                                    setSelectedIds(prevState =>
                                        prevState.filter(selectedId => !currentPageIds.includes(selectedId))
                                    )
                                } else {
                                    setSelectedIds(prevState => [
                                        ...prevState.filter(selectedId => !currentPageIds.includes(selectedId)),
                                        ...currentPageIds
                                    ])
                                }
                            }}
                        >
                            {t('commons:this_page')}
                            <span>{t('commons:x_rows', {count: currentPageIds.length})}</span>
                        </StyledSelectMenuItem>
                        <StyledSelectMenuItem
                            onClick={() => {
                                setSelectedIds([])
                                setUnselectedIds([])
                                setIsSelectAll(selectedCount != paginationTotal)
                            }}
                        >
                            {t('commons:all_pages')}
                            <span>{t('commons:x_rows', {count: paginationTotal})}</span>
                        </StyledSelectMenuItem>
                    </MenuGroup>
                </Menu>
            </MenuProvider>
        </Flexbox>
    )
}
